import React, {Component} from 'react';
import {action, observable} from 'mobx';
import {inject, observer} from 'mobx-react';
import {Button, FormSelect, FormInput} from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import {toast} from 'react-toastify';
import moment from 'moment';
import PageTitle from '../../../components/common/PageTitle';
import './style.scss';

const TABS = [
  {key: 'review',  label: '리뷰',  icon: 'message-square'},
  {key: 'order',   label: '주문',  icon: 'shopping-cart'},
  {key: 'product', label: '상품',  icon: 'package'}
];

const PAGE_LIMIT_OPTIONS = [20, 50, 100, 200, 500, 1000, 2000];

const ALL_STORES_VALUE = '__all__';

// 탭별 처리 작업 정의 — 추후 확장
const ISSUE_TYPES = {
  review: [
    {
      key: 'literalNewline',
      label: '리터럴 \\n 제거 대상',
      desc: '내용에 백슬래시+n 문자열이 포함된 리뷰만 조회합니다.',
      icon: 'alert-triangle',
      searchParams: {searchType: 'review', keyword: '\\n'}
    },
    {
      key: 'invalidStarRating',
      label: '별점 입력 오류 개선',
      desc: '별점/배송별점이 1~5(0~100 환산 누락) 또는 100 초과인 리뷰를 정상화 (1→20, 2→40, 3→60, 4→80, 5→100, >100→100).',
      icon: 'star',
      searchParams: {invalidStarRating: true}
    }
  ],
  order: [],
  product: []
};

@inject(({teamUser}) => ({isLogin: teamUser.isLogin, me: teamUser.me}))
@inject(({AOA}) => ({
  getShopList: AOA.getShopList,
  getDataMigrationReviewList: AOA.getDataMigrationReviewList,
  getDataMigrationOrderList: AOA.getDataMigrationOrderList,
  getDataMigrationProductList: AOA.getDataMigrationProductList,
}))
@inject(({review}) => ({updateReview: review.updateReview, fixReviewContents: review.fixReviewContents, fixReviewStarRating: review.fixReviewStarRating}))
@observer
class DataMigration extends Component {

  @observable shops = [];
  @observable selectedStoreId = '';
  @observable selectedShopNo = '';
  @observable activeTab = 'review';

  @observable list = {docs: [], totalDocs: 0, page: 1, totalPages: 1};
  @observable loading = false;
  @observable page = 1;
  @observable limit = 20;
  @observable storeKeyword = '';
  // 정렬: '' (기본 displayAt), 'goodsPt', 'deliveryPt' 등 / 방향: 'desc' | 'asc'
  @observable sortField = '';
  @observable sortDirection = 'desc';

  // 활성화된 처리 작업 키 (예: 'literalNewline')
  @observable activeIssueKey = null;

  // 일괄 처리용 체크 상태
  @observable selectedIds = {};      // {reviewId: true}
  @observable bulkRunning = false;
  @observable bulkProgress = {done: 0, total: 0, fail: 0};

  componentDidMount() {
    this.loadShops();
  }

  // 샵 docs를 store_id 기준으로 그룹핑
  groupedStores = () => {
    const map = new Map();
    (this.shops || []).forEach(s => {
      const storeObj = (s.store_id && typeof s.store_id === 'object') ? s.store_id : null;
      const storeId = storeObj ? storeObj._id : s.store_id;
      if (!storeId) return;
      if (!map.has(storeId)) {
        map.set(storeId, {
          storeId,
          store: storeObj || {_id: storeId},
          shops: []
        });
      }
      map.get(storeId).shops.push(s);
    });
    return Array.from(map.values()).sort((a, b) =>
      (this._storeName(a.store) || '').localeCompare(this._storeName(b.store) || '')
    );
  };

  filteredStores = () => {
    const list = this.groupedStores();
    const kw = (this.storeKeyword || '').trim().toLowerCase();
    if (!kw) return list;
    return list.filter(g => {
      const label = `${this._storeName(g.store)} ${g.store.email || ''} ${g.store.domain || ''}`.toLowerCase();
      return label.includes(kw);
    });
  };

  selectedStoreGroup = () => {
    if (this.selectedStoreId === ALL_STORES_VALUE) return null;
    return this.groupedStores().find(g => g.storeId === this.selectedStoreId) || null;
  };

  selectedShop = () => {
    if (this.selectedStoreId === ALL_STORES_VALUE) return null;
    const g = this.selectedStoreGroup();
    if (!g) return null;
    return g.shops.find(s => String(this._shopNoOf(s)) === String(this.selectedShopNo)) || null;
  };

  isAllStoresMode = () => this.selectedStoreId === ALL_STORES_VALUE;

  // 리뷰/주문/상품 doc에서 store_id 추출 (populated object 또는 string 둘 다 지원)
  _storeIdOf = (doc) => {
    if (!doc) return null;
    const v = doc.store_id;
    if (!v) return null;
    if (typeof v === 'object') return v._id || null;
    return v;
  };

  // doc.store_id 의 표시용 라벨 (이름 우선, fallback 으로 _id)
  _storeDisplayOf = (doc) => {
    const v = doc && doc.store_id;
    if (!v) return null;
    if (typeof v === 'object') {
      return {
        name: v.name || v.email || '(이름없음)',
        sub: v.platformId || v.domain || '',
        id: v._id || ''
      };
    }
    return {name: String(v), sub: '', id: String(v)};
  };

  _storeName = (store) => {
    if (!store) return '';
    return store.name || store.companyName || store.email || store._id || '(이름없음)';
  };

  _storeLabel = (group) => {
    const name = this._storeName(group.store);
    const sub = group.store.email || group.store.domain || '';
    return `${name}${sub ? ` · ${sub}` : ''} · ${group.shops.length}개 샵`;
  };

  _shopNoOf = (shop) => {
    return shop.platformShopNo != null ? shop.platformShopNo : (shop.shopNo != null ? shop.shopNo : 1);
  };

  _shopLabel = (shop) => {
    if (!shop) return '';
    const name = shop.shopName || shop.mallId || '(이름없음)';
    return `${name} (Shop#${this._shopNoOf(shop)})`;
  };

  @action loadShops = async () => {
    try {
      const res = await this.props.getShopList({
        page: 1,
        limit: 1000,
        sort: 'shopName',
        searchParams: {isPaidUser: true}
      });
      const docs = (res && res.docs) ? res.docs : [];
      this.shops = docs;
    } catch (e) {
      console.error(e);
      toast.error('샵 목록을 불러오지 못했습니다.');
    }
  };

  @action onChangeStore = (e) => {
    this.selectedStoreId = e.target.value;
    if (this.selectedStoreId === ALL_STORES_VALUE) {
      // 전체 모드 — 샵 선택 비활성
      this.selectedShopNo = ALL_STORES_VALUE;
    } else {
      // 스토어가 바뀌면 첫 번째 샵 자동 선택
      const g = this.selectedStoreGroup();
      if (g && g.shops.length > 0) {
        this.selectedShopNo = String(this._shopNoOf(g.shops[0]));
      } else {
        this.selectedShopNo = '';
      }
    }
    this.page = 1;
    this.selectedIds = {};
    this.loadList();
  };

  @action onChangeShop = (e) => {
    this.selectedShopNo = e.target.value;
    this.page = 1;
    this.selectedIds = {};
    this.loadList();
  };

  // 리터럴 "\n" (백슬래시+n 2글자) 포함 여부
  hasLiteralNewline = (s) => typeof s === 'string' && s.indexOf('\\n') > -1;

  // 별점값이 비정상인지 판정 (1~5 또는 100 초과)
  isInvalidStarValue = (v) => {
    if (v == null) return false;
    const n = Number(v);
    if (isNaN(n)) return false;
    if (n >= 1 && n <= 5) return true;
    if (n > 100) return true;
    return false;
  };

  // 리뷰에 별점 입력 오류가 있는지
  hasInvalidStarRating = (doc) => {
    if (!doc) return false;
    return this.isInvalidStarValue(doc.goodsPt) || this.isInvalidStarValue(doc.deliveryPt);
  };

  // 별점값 정상화: 1→20, 2→40, 3→60, 4→80, 5→100, >100→100, 그 외는 변경 없음
  normalizeStarValue = (v) => {
    if (v == null) return v;
    const n = Number(v);
    if (isNaN(n)) return v;
    if (n >= 1 && n <= 5) return n * 20;
    if (n > 100) return 100;
    return n;
  };

  // 일괄 별점 정상화
  @action bulkFixStarRating = async () => {
    const ids = Object.keys(this.selectedIds).filter(k => this.selectedIds[k]);
    if (ids.length === 0) {
      toast.info('선택된 리뷰가 없습니다.');
      return;
    }
    if (!window.confirm(`선택한 ${ids.length}건의 리뷰 별점을 정상화합니다. (1→20, 2→40, ..., >100→100)\n진행할까요?`)) return;

    const docsById = {};
    (this.list.docs || []).forEach(d => { docsById[d._id] = d; });

    this.bulkRunning = true;
    this.bulkProgress = {done: 0, total: ids.length, fail: 0};

    for (const id of ids) {
      const doc = docsById[id];
      if (!doc || !this.hasInvalidStarRating(doc)) {
        this.bulkProgress.done += 1;
        continue;
      }
      const update = {};
      if (this.isInvalidStarValue(doc.goodsPt)) {
        update.goodsPt = this.normalizeStarValue(doc.goodsPt);
      }
      if (this.isInvalidStarValue(doc.deliveryPt)) {
        update.deliveryPt = this.normalizeStarValue(doc.deliveryPt);
      }
      if (Object.keys(update).length === 0) {
        this.bulkProgress.done += 1;
        continue;
      }
      try {
        const res = await this.props.fixReviewStarRating({
          review_id: doc._id,
          goodsPt: update.goodsPt,
          deliveryPt: update.deliveryPt,
        });
        if (res && res.errors && res.errors.length > 0) {
          this.bulkProgress.fail += 1;
        } else {
          const idx = this.list.docs.findIndex(d => d._id === doc._id);
          if (idx > -1) {
            if (update.goodsPt !== undefined) this.list.docs[idx].goodsPt = update.goodsPt;
            if (update.deliveryPt !== undefined) this.list.docs[idx].deliveryPt = update.deliveryPt;
          }
          delete this.selectedIds[id];
        }
      } catch (e) {
        console.error(e);
        this.bulkProgress.fail += 1;
      }
      this.bulkProgress.done += 1;
    }

    const okCount = this.bulkProgress.done - this.bulkProgress.fail;
    if (this.bulkProgress.fail === 0) {
      toast.success(`별점 정상화 완료 (${okCount}건)`);
    } else {
      toast.error(`처리 실패 ${this.bulkProgress.fail}건 / 성공 ${okCount}건`);
    }
    this.bulkRunning = false;
  };

  @action onChangeTab = (key) => {
    if (this.activeTab === key) return;
    this.activeTab = key;
    this.activeIssueKey = null;
    this.page = 1;
    this.selectedIds = {};
    // 탭별로 사용 가능한 정렬 필드가 다르므로 리셋
    this.sortField = '';
    this.sortDirection = 'desc';
    this.loadList();
  };

  currentIssue = () => {
    const issues = ISSUE_TYPES[this.activeTab] || [];
    return issues.find(it => it.key === this.activeIssueKey) || null;
  };

  @action toggleIssue = (issueKey) => {
    this.activeIssueKey = (this.activeIssueKey === issueKey) ? null : issueKey;
    this.page = 1;
    this.selectedIds = {};
    this.loadList();
  };

  // === 체크박스/일괄 처리 ===
  // 행이 현재 활성 issue 의 처리 대상인지
  rowHasIssue = (doc) => {
    if (!doc) return false;
    if (this.activeIssueKey === 'literalNewline') return this.hasLiteralNewline(doc.contents);
    if (this.activeIssueKey === 'invalidStarRating') return this.hasInvalidStarRating(doc);
    return false;
  };

  selectableDocs = () => {
    if (this.activeTab !== 'review') return [];
    if (!this.activeIssueKey) return [];
    return (this.list.docs || []).filter(d => this.rowHasIssue(d));
  };

  selectedCount = () => Object.keys(this.selectedIds).filter(k => this.selectedIds[k]).length;

  isAllSelected = () => {
    const sel = this.selectableDocs();
    if (sel.length === 0) return false;
    return sel.every(d => !!this.selectedIds[d._id]);
  };

  @action toggleOne = (id) => {
    this.selectedIds[id] = !this.selectedIds[id];
    if (!this.selectedIds[id]) delete this.selectedIds[id];
  };

  @action toggleAll = () => {
    const sel = this.selectableDocs();
    if (this.isAllSelected()) {
      sel.forEach(d => { delete this.selectedIds[d._id]; });
    } else {
      sel.forEach(d => { this.selectedIds[d._id] = true; });
    }
  };

  @action bulkFixNewline = async () => {
    const ids = Object.keys(this.selectedIds).filter(k => this.selectedIds[k]);
    if (ids.length === 0) {
      toast.info('선택된 리뷰가 없습니다.');
      return;
    }
    if (!window.confirm(`선택한 ${ids.length}건의 리뷰에서 리터럴 \\n을 실제 줄바꿈으로 치환하고 카페24 게시글에도 반영합니다. 진행할까요?`)) return;

    const docsById = {};
    (this.list.docs || []).forEach(d => { docsById[d._id] = d; });

    this.bulkRunning = true;
    this.bulkProgress = {done: 0, total: ids.length, fail: 0};

    let cafe24FailCount = 0;
    for (const id of ids) {
      const doc = docsById[id];
      if (!doc || !this.hasLiteralNewline(doc.contents)) {
        this.bulkProgress.done += 1;
        continue;
      }
      try {
        const fixed = doc.contents.replace(/\\n/g, '\n');
        const storeIdForFix = this.isAllStoresMode() ? this._storeIdOf(doc) : this.selectedStoreId;
        if (!storeIdForFix) {
          this.bulkProgress.fail += 1;
          this.bulkProgress.done += 1;
          continue;
        }
        const res = await this.props.fixReviewContents({
          store_id: storeIdForFix,
          review_id: doc._id,
          contents: fixed
        });
        if (res && res.errors && res.errors.length > 0) {
          this.bulkProgress.fail += 1;
        } else {
          // 로컬 갱신
          const idx = this.list.docs.findIndex(d => d._id === doc._id);
          if (idx > -1) this.list.docs[idx].contents = fixed;
          delete this.selectedIds[id];
          if (res && res.data && !res.data.cafe24Updated && doc.platformArticleNo) {
            cafe24FailCount += 1;
          }
        }
      } catch (e) {
        console.error(e);
        this.bulkProgress.fail += 1;
      }
      this.bulkProgress.done += 1;
    }

    const okCount = this.bulkProgress.done - this.bulkProgress.fail;
    if (this.bulkProgress.fail === 0) {
      if (cafe24FailCount === 0) {
        toast.success(`일괄 처리 완료 (${okCount}건, 카페24 반영됨)`);
      } else {
        toast.warn(`${okCount}건 처리 완료. 카페24 반영 실패 ${cafe24FailCount}건`);
      }
    } else {
      toast.error(`처리 실패 ${this.bulkProgress.fail}건 / 성공 ${okCount}건`);
    }
    this.bulkRunning = false;
  };

  @action onChangeLimit = (e) => {
    this.limit = parseInt(e.target.value, 10) || 20;
    this.page = 1;
    this.loadList();
  };

  // 컬럼 헤더 클릭 정렬: 같은 필드면 방향 토글, 다른 필드면 desc 시작.
  // 세 번째 클릭은 기본(displayAt desc)으로 리셋.
  @action setSort = (field) => {
    if (this.sortField === field) {
      if (this.sortDirection === 'desc') {
        this.sortDirection = 'asc';
      } else {
        // asc → 기본으로 리셋
        this.sortField = '';
        this.sortDirection = 'desc';
      }
    } else {
      this.sortField = field;
      this.sortDirection = 'desc';
    }
    this.page = 1;
    this.loadList();
  };

  sortIndicator = (field) => {
    if (this.sortField !== field) return '';
    return this.sortDirection === 'desc' ? ' ▼' : ' ▲';
  };

  @action goPage = (n) => {
    if (n < 1 || n > (this.list.totalPages || 1)) return;
    this.page = n;
    this.loadList();
  };

  @action loadList = async () => {
    const isAllStores = this.isAllStoresMode();

    if (!isAllStores) {
      const shop = this.selectedShop();
      if (!shop || !this.selectedStoreId) {
        this.list = {docs: [], totalDocs: 0, page: 1, totalPages: 1};
        return;
      }
    } else if (!this.selectedStoreId) {
      this.list = {docs: [], totalDocs: 0, page: 1, totalPages: 1};
      return;
    }

    const issue = this.currentIssue();
    const issueSearch = (issue && issue.searchParams) ? issue.searchParams : {};

    const baseParams = {
      page: this.page,
      limit: this.limit,
      sort: this.sortField ? (this.sortDirection === 'desc' ? `-${this.sortField}` : this.sortField) : '',
      searchParams: Object.assign({}, issueSearch)
    };
    if (!isAllStores) {
      const shop = this.selectedShop();
      baseParams.store_id = this.selectedStoreId;
      baseParams.shopNo = this._shopNoOf(shop);
    }

    this.loading = true;
    try {
      let res;
      if (this.activeTab === 'review') {
        res = await this.props.getDataMigrationReviewList(baseParams);
      } else if (this.activeTab === 'order') {
        res = await this.props.getDataMigrationOrderList(baseParams);
      } else if (this.activeTab === 'product') {
        res = await this.props.getDataMigrationProductList(baseParams);
      }
      this.list = (res && res.docs) ? res : {docs: [], totalDocs: 0, page: 1, totalPages: 1};
    } catch (e) {
      console.error(e);
      toast.error('데이터를 불러오지 못했습니다.');
      this.list = {docs: [], totalDocs: 0, page: 1, totalPages: 1};
    } finally {
      this.loading = false;
    }
  };

  renderTable = () => {
    const docs = this.list.docs || [];
    if (docs.length === 0) {
      return (
        <div className="dm-empty">
          <FeatherIcon icon="inbox" size="36"/>
          <div>
            {this.selectedStoreId && this.selectedShopNo
              ? (this.activeIssueKey ? '해당 조건의 데이터가 없습니다.' : '데이터가 없습니다.')
              : '먼저 스토어와 샵을 선택하세요.'}
          </div>
        </div>
      );
    }
    if (this.activeTab === 'review') return this.renderReviewTable(docs);
    if (this.activeTab === 'order')  return this.renderOrderTable(docs);
    if (this.activeTab === 'product') return this.renderProductTable(docs);
    return null;
  };

  renderReviewTable = (docs) => {
    const statusLabel = (s) => s === 2 ? '게시중' : (s === 1 ? '보류' : (s != null ? s : '-'));
    const allChecked = this.isAllSelected();
    const hasSelectable = this.selectableDocs().length > 0;
    const sortableHeaderStyle = {cursor: 'pointer', userSelect: 'none'};
    return (
      <table className="dm-table">
        <thead>
        <tr>
          <th style={{width: 36}}>
            {hasSelectable && (
              <input type="checkbox"
                     checked={allChecked}
                     onChange={this.toggleAll}
                     title="문제 항목 전체 선택/해제"/>
            )}
          </th>
          <th style={{width: 50}}>#</th>
          <th>내용</th>
          <th style={{width: 140}}>작성자</th>
          <th style={{width: 80, ...sortableHeaderStyle}}
              onClick={() => this.setSort('goodsPt')}
              title="별점으로 정렬"
              className={this.sortField === 'goodsPt' ? 'dm-sort-active' : ''}>
            별점{this.sortIndicator('goodsPt')}
          </th>
          <th style={{width: 90, ...sortableHeaderStyle}}
              onClick={() => this.setSort('deliveryPt')}
              title="배송별점으로 정렬"
              className={this.sortField === 'deliveryPt' ? 'dm-sort-active' : ''}>
            배송별점{this.sortIndicator('deliveryPt')}
          </th>
          <th style={{width: 90}}>입력경로</th>
          <th style={{width: 80}}>상태</th>
          <th style={{width: 160}}>작성일</th>
          <th style={{width: 180}}>스토어</th>
          <th style={{width: 200}}>_id</th>
        </tr>
        </thead>
        <tbody>
        {docs.map((d, i) => {
          const hasIssue = this.rowHasIssue(d);
          const isNewlineIssue = this.activeIssueKey === 'literalNewline' && this.hasLiteralNewline(d.contents);
          const isStarIssue = this.activeIssueKey === 'invalidStarRating' && this.hasInvalidStarRating(d);
          const goodsPtInvalid = this.isInvalidStarValue(d.goodsPt);
          const deliveryPtInvalid = this.isInvalidStarValue(d.deliveryPt);
          const checked = !!this.selectedIds[d._id];
          return (
            <tr key={d._id || i} className={hasIssue ? 'dm-row-issue' : ''}>
              <td>
                {hasIssue && (
                  <input type="checkbox"
                         checked={checked}
                         disabled={this.bulkRunning}
                         onChange={() => this.toggleOne(d._id)}/>
                )}
              </td>
              <td>{(this.page - 1) * this.limit + i + 1}</td>
              <td className="dm-cell-text" title={d.contents || ''}>
                {isNewlineIssue && <span className="dm-issue-chip">\n</span>}
                {d.contents || '-'}
              </td>
              <td>
                {d.writerName || '-'}
                {d.writerId && <span className="dm-sub"> ({d.writerId})</span>}
              </td>
              <td>
                {d.goodsPt != null ? d.goodsPt : '-'}
                {isStarIssue && goodsPtInvalid && (
                  <span className="dm-issue-chip" title={`정상화 후: ${this.normalizeStarValue(d.goodsPt)}`}>!</span>
                )}
              </td>
              <td>
                {d.deliveryPt != null ? d.deliveryPt : '-'}
                {isStarIssue && deliveryPtInvalid && (
                  <span className="dm-issue-chip" title={`정상화 후: ${this.normalizeStarValue(d.deliveryPt)}`}>!</span>
                )}
              </td>
              <td>{d.fromWrite || '-'}</td>
              <td>{statusLabel(d.displayStatus)}</td>
              <td>{d.writerAt || d.createdAt ? moment(d.writerAt || d.createdAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
              <td className="dm-cell-text" title={(this._storeDisplayOf(d) || {}).id || ''}>
                {(() => {
                  const s = this._storeDisplayOf(d);
                  if (!s) return '-';
                  return (
                    <>
                      <div>{s.name}</div>
                      {s.sub && <div className="dm-sub">{s.sub}</div>}
                    </>
                  );
                })()}
              </td>
              <td className="dm-cell-mono">{d._id}</td>
            </tr>
          );
        })}
        </tbody>
      </table>
    );
  };

  renderOrderTable = (docs) => (
    <table className="dm-table">
      <thead>
      <tr>
        <th style={{width: 60}}>#</th>
        <th>주문번호</th>
        <th style={{width: 120}}>구매자</th>
        <th style={{width: 100}}>주문상태</th>
        <th style={{width: 100}}>금액</th>
        <th style={{width: 160}}>주문일</th>
        <th style={{width: 200}}>_id</th>
      </tr>
      </thead>
      <tbody>
      {docs.map((d, i) => (
        <tr key={d._id || i}>
          <td>{(this.page - 1) * this.limit + i + 1}</td>
          <td className="dm-cell-text">{d.order_id || d.orderId || '-'}</td>
          <td>{d.buyerName || d.buyer_id || '-'}</td>
          <td>{d.orderStatus || d.status || '-'}</td>
          <td>{d.totalPrice != null ? d.totalPrice.toLocaleString() : '-'}</td>
          <td>{d.orderedAt || d.createdAt ? moment(d.orderedAt || d.createdAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
          <td className="dm-cell-mono">{d._id}</td>
        </tr>
      ))}
      </tbody>
    </table>
  );

  renderProductTable = (docs) => (
    <table className="dm-table">
      <thead>
      <tr>
        <th style={{width: 60}}>#</th>
        <th style={{width: 70}}>이미지</th>
        <th>상품명</th>
        <th style={{width: 100}}>플랫폼번호</th>
        <th style={{width: 100}}>가격</th>
        <th style={{width: 80}}>리뷰수</th>
        <th style={{width: 200}}>_id</th>
      </tr>
      </thead>
      <tbody>
      {docs.map((d, i) => (
        <tr key={d._id || i}>
          <td>{(this.page - 1) * this.limit + i + 1}</td>
          <td>
            {d.image ? <img className="dm-thumb" src={d.image} alt={d.name}/> : '-'}
          </td>
          <td className="dm-cell-text">{d.name || d.productName || '-'}</td>
          <td>{d.product_no || d.platformProductNo || '-'}</td>
          <td>{d.price != null ? d.price.toLocaleString() : '-'}</td>
          <td>{d.reviewCount != null ? d.reviewCount : '-'}</td>
          <td className="dm-cell-mono">{d._id}</td>
        </tr>
      ))}
      </tbody>
    </table>
  );

  render() {
    const totalPages = this.list.totalPages || 1;
    const stores = this.filteredStores();
    const allStoresCount = this.groupedStores().length;
    const currentStoreGroup = this.selectedStoreGroup();
    const currentShops = currentStoreGroup ? currentStoreGroup.shops : [];

    return (
      <div className="main-content">
        <PageTitle/>
        <div className="widget-box data-migration-page">

          <div className="page-head">
            <p>스토어를 선택한 후 보유한 샵을 선택하고, 데이터 모델 탭을 누르면 해당 데이터 목록이 표시됩니다. (1차: 조회만)</p>
          </div>

          <div className="dm-shop-bar">
            <div className="dm-shop-bar__label">
              <FeatherIcon icon="business" size="16"/>
              <span>스토어 선택</span>
            </div>
            <FormInput placeholder="스토어 이름/이메일/도메인 검색"
                       value={this.storeKeyword}
                       onChange={(e) => this.storeKeyword = e.target.value}
                       style={{maxWidth: 240}}/>
            <FormSelect value={this.selectedStoreId} onChange={this.onChangeStore} style={{maxWidth: 480}}>
              <option value="">— 스토어를 선택하세요 ({allStoresCount}개) —</option>
              <option value={ALL_STORES_VALUE}>★ 전체 스토어 ({allStoresCount}개 · 모든 샵)</option>
              {stores.map((g) => (
                <option key={g.storeId} value={g.storeId}>{this._storeLabel(g)}</option>
              ))}
            </FormSelect>
            <Button theme="light" size="sm" onClick={this.loadShops}>
              <FeatherIcon icon="refresh-cw" size="14"/> 새로고침
            </Button>
          </div>

          <div className="dm-shop-bar">
            <div className="dm-shop-bar__label">
              <FeatherIcon icon="shopping-bag" size="16"/>
              <span>샵 선택</span>
            </div>
            <FormSelect value={this.selectedShopNo}
                        onChange={this.onChangeShop}
                        disabled={!this.selectedStoreId || this.isAllStoresMode()}
                        style={{maxWidth: 480}}>
              {!this.selectedStoreId && <option value="">— 먼저 스토어를 선택하세요 —</option>}
              {this.isAllStoresMode() && <option value={ALL_STORES_VALUE}>모든 샵 (전체 스토어 모드)</option>}
              {!this.isAllStoresMode() && this.selectedStoreId && currentShops.length === 0 && <option value="">샵이 없습니다</option>}
              {!this.isAllStoresMode() && currentShops.map((s) => (
                <option key={this._shopNoOf(s)} value={String(this._shopNoOf(s))}>{this._shopLabel(s)}</option>
              ))}
            </FormSelect>
            {this.selectedStoreId && !this.isAllStoresMode() && (
              <span className="dm-shop-count">{currentShops.length}개 샵</span>
            )}
            {this.isAllStoresMode() && (
              <span className="dm-shop-count">전체 모드</span>
            )}
          </div>

          <div className="dm-tabs">
            {TABS.map(t => (
              <div key={t.key}
                   className={`dm-tab ${this.activeTab === t.key ? 'is-active' : ''}`}
                   onClick={() => this.onChangeTab(t.key)}>
                <FeatherIcon icon={t.icon} size="16"/>
                <span>{t.label}</span>
              </div>
            ))}
          </div>

          {/* === 처리 작업: 탭별 issue 토글 — 클릭 시 해당 조건으로 서버 재조회 === */}
          {(ISSUE_TYPES[this.activeTab] || []).length > 0 && (
            <div className="dm-issue-bar">
              <div className="dm-issue-bar__label">
                <FeatherIcon icon="tool" size="14"/>
                <span>처리 작업</span>
              </div>
              <div className="dm-issue-bar__list">
                {(ISSUE_TYPES[this.activeTab] || []).map(it => {
                  const active = this.activeIssueKey === it.key;
                  return (
                    <button key={it.key}
                            type="button"
                            className={`dm-issue-btn ${active ? 'is-active' : ''}`}
                            title={it.desc}
                            onClick={() => this.toggleIssue(it.key)}
                            disabled={!this.selectedStoreId || !this.selectedShopNo}>
                      <FeatherIcon icon={it.icon || 'alert-triangle'} size="13"/>
                      <span>{it.label}</span>
                      {active && <FeatherIcon icon="x" size="12"/>}
                    </button>
                  );
                })}
              </div>
              {this.activeIssueKey && (
                <div className="dm-issue-hint">
                  <FeatherIcon icon="info" size="12"/>
                  <span>
                    안정적인 처리를 위해 <b>체크박스로 선택한 리뷰만</b> 일괄 처리됩니다.
                    문제 항목 옆 체크박스를 선택한 뒤 상단의 <b>일괄 처리 버튼</b>을 눌러주세요.
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="dm-toolbar">
            <div className="dm-toolbar__left">
              <span>전체 <b>{this.list.totalDocs || 0}</b>건</span>
              {this.activeIssueKey && this.currentIssue() && (
                <span className="dm-active-issue">
                  <FeatherIcon icon="filter" size="12"/>
                  필터: {this.currentIssue().label}
                </span>
              )}
              {this.activeTab === 'review' && this.selectedCount() > 0 && (
                <span className="dm-selected-count">
                  <FeatherIcon icon="check-square" size="12"/>
                  선택: <b>{this.selectedCount()}</b>건
                </span>
              )}
              {this.bulkRunning && (
                <span className="dm-loading">
                  <FeatherIcon icon="loader" size="13"/>
                  일괄 처리 중 {this.bulkProgress.done}/{this.bulkProgress.total}
                  {this.bulkProgress.fail > 0 && ` (실패 ${this.bulkProgress.fail})`}
                </span>
              )}
              {this.loading && !this.bulkRunning && (
                <span className="dm-loading"><FeatherIcon icon="loader" size="13"/> 로딩 중…</span>
              )}
            </div>
            <div className="dm-toolbar__right">
              {this.activeTab === 'review' && this.selectedCount() > 0 && this.activeIssueKey === 'literalNewline' && (
                <Button theme="warning" size="sm"
                        disabled={this.bulkRunning}
                        onClick={this.bulkFixNewline}>
                  <FeatherIcon icon={this.bulkRunning ? 'loader' : 'tool'} size="14"/>
                  {this.bulkRunning ? ' 처리 중…' : ` 선택 ${this.selectedCount()}건 줄바꿈 일괄 수정`}
                </Button>
              )}
              {this.activeTab === 'review' && this.selectedCount() > 0 && this.activeIssueKey === 'invalidStarRating' && (
                <Button theme="warning" size="sm"
                        disabled={this.bulkRunning}
                        onClick={this.bulkFixStarRating}>
                  <FeatherIcon icon={this.bulkRunning ? 'loader' : 'star'} size="14"/>
                  {this.bulkRunning ? ' 처리 중…' : ` 선택 ${this.selectedCount()}건 별점 일괄 정상화`}
                </Button>
              )}
              <span>페이지당</span>
              <FormSelect value={this.limit} onChange={this.onChangeLimit} style={{width: 80}}>
                {PAGE_LIMIT_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </FormSelect>
              <Button theme="light" size="sm" onClick={this.loadList} disabled={!this.selectedStoreId || !this.selectedShopNo || this.bulkRunning}>
                <FeatherIcon icon="refresh-cw" size="14"/> 다시조회
              </Button>
            </div>
          </div>

          <div className="dm-table-wrap">
            {this.renderTable()}
          </div>

          {this.list.docs && this.list.docs.length > 0 && (
            <div className="dm-pagination">
              <Button theme="light" size="sm" disabled={this.page <= 1} onClick={() => this.goPage(this.page - 1)}>
                <FeatherIcon icon="chevron-left" size="14"/> 이전
              </Button>
              <span>{this.page} / {totalPages}</span>
              <Button theme="light" size="sm" disabled={this.page >= totalPages} onClick={() => this.goPage(this.page + 1)}>
                다음 <FeatherIcon icon="chevron-right" size="14"/>
              </Button>
            </div>
          )}

        </div>
      </div>
    );
  }
}

export default DataMigration;
